import { useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
// import { Dialog, DialogContent, DialogDescription, DialogFooter } from '@/components/ui/dialog';
// import { DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/Dialogs/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useExperiment } from '@/state/experiments/useExperiment';
import { apiErrorMessage } from '@/state/api-error';
import { DuplicateWebsiteSelect, useDuplicateTarget } from '../DuplicateWebsiteSelect';

export type ActionModalsTypes = 'delete' | 'archive' | 'duplicate';
type ActionModalsProps = {
  experiment: any;
  dialogAction: ActionModalsTypes;
  setDialogAction: (action: ActionModalsTypes | null) => void;
  onClose: () => void;
};

export const ActionModals = (props: ActionModalsProps) => {
  const { experiment, dialogAction, setDialogAction, onClose } = props;
  const { id, name } = experiment;
  const [duplicateName, setDuplicateName] = useState(name + ' (copy)');
  const { deleteExperiment, archiveExperiment, duplicateExperiment } = useExperiment(0);
  const duplicateTarget = useDuplicateTarget({ experimentWebsiteId: experiment.websiteId });
  const isOpen = !!dialogAction;
  const { title, text } = DIALOG_DATA[dialogAction] || {};

  /**
   * Delete, archive and duplicate all land here, so a throw escaping this handler left
   * ConfirmDialog's Confirm button disabled for good - it re-enables after the await and has
   * no `finally`. The user was left staring at a dialog with a dead primary button.
   *
   * The dialog stays open on failure, which matters for duplicate: the name typed for the
   * copy is still in the field.
   */
  const onConfirm = async () => {
    try {
      if (dialogAction === 'duplicate') {
        await duplicateExperiment(id, duplicateName, duplicateTarget.targetWebsiteId);
        // The list shows the website selected in the header, so say where the copy went.
        if (duplicateTarget.isMove) toast.success(`Copied to ${duplicateTarget.targetUrl ?? 'the other website'}.`);
      } else if (dialogAction === 'archive') {
        await archiveExperiment(id);
      } else {
        await deleteExperiment(id);
      }
      onClose();
    } catch (error) {
      // apiErrorMessage, not instanceof Error: the API client throws a plain object, and
      // a 404 for a website outside the account reaches no global dialog.
      toast.error(apiErrorMessage(error, FALLBACK_MESSAGE[dialogAction]));
    }
  };

  return (
    <ConfirmDialog
      open={true}
      onConfirm={onConfirm}
      onCancel={() => setDialogAction(null)}
      title={title}
      description={text}
      disabled={(dialogAction === 'duplicate' && !duplicateName) || false}
    >
      {/* The id is per-row, and prefixed with the screen: `htmlFor` was pointing at a
          `name` id that no element carried, and because `for` wins over the wrapping,
          the label named nothing at all. Home's duplicate dialog labels the same
          field, so the prefix keeps the two from ever colliding. */}
      {dialogAction === 'duplicate' && (
        <>
          <Label htmlFor={`library-duplicate-name-${id}`} className="my-4 grid gap-2">
            New Experiment Name:
            <Input
              id={`library-duplicate-name-${id}`}
              value={duplicateName}
              onChange={(ev) => setDuplicateName(ev.target.value)}
            />
          </Label>
          <DuplicateWebsiteSelect
            id={`library-duplicate-website-${id}`}
            value={duplicateTarget.targetWebsiteId}
            onChange={duplicateTarget.setTargetWebsiteId}
          />
        </>
      )}
    </ConfirmDialog>
  );
};

/** Only reached when the thrown value is not an Error - a request rejection, say. */
const FALLBACK_MESSAGE: Record<ActionModalsTypes, string> = {
  duplicate: 'Could not duplicate the experiment.',
  archive: 'Could not archive the experiment.',
  delete: 'Could not delete the experiment.',
};

const DIALOG_DATA = {
  duplicate: {
    title: 'Duplicate Experiment',
    text: 'Are you sure you want to duplicate this experiment?',
  },
  archive: {
    title: 'Archive Experiment',
    text: 'Are you sure you want to archive this experiment?',
  },
  delete: {
    title: 'Delete Experiment',
    text: 'Are you sure you want to delete this experiment?',
  },
};
